/**
 * Schedules a silent token refresh 60 seconds before the bearer token expires.
 *
 * Returns a cancel function. Call it when the session ends.
 */

/** How many ms before expiry to fire the refresh. */
const REFRESH_BUFFER_MS = 60 * 1_000

/**
 * Parses the backend's expiration field to a Unix timestamp in milliseconds.
 *
 * Handles three formats:
 *   - ISO 8601 string:            "2024-01-20T10:30:00.000Z"
 *   - Unix timestamp in seconds:  "1705748000"   (10 digits)
 *   - Unix timestamp in ms:       "1705748000000" (13 digits)
 */
export function parseExpiryMs(expiration: string): number {
  // 1. ISO 8601
  const isoMs = new Date(expiration).getTime()
  if (!isNaN(isoMs) && isoMs > 0) return isoMs

  // 2. Plain numeric string
  const num = Number(expiration)
  if (isNaN(num)) return NaN

  // Unix seconds (10 digits)
  if (num > 1_000_000_000 && num < 10_000_000_000) return num * 1_000

  // Already milliseconds (13 digits)
  if (num > 1_000_000_000_000) return num

  return NaN
}

export function scheduleRefresh(
  expiration: string,
  onRefresh: () => void,
): () => void {
  const expiryMs = parseExpiryMs(expiration)
  if (isNaN(expiryMs)) return () => {}

  const delay = expiryMs - REFRESH_BUFFER_MS - Date.now()
  if (delay <= 0) return () => {}

  const timer = setTimeout(onRefresh, delay)
  return () => clearTimeout(timer)
}
